import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { CircularProgress, FormHelperText, Grid } from "@mui/material";
import axios from "axios";
import { useFormik } from "formik";
import * as yup from "yup";

import CommonButton from "../../common/button/Button";
import CommonInput from "../../common/input/InputBox";
import UploadImage from "../../common/uploadImage/UploadImage";
import { DatePicker } from "@mui/x-date-pickers";
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import "./ticketForm.css";
// import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
// import DateFnsUtils from "@date-io/date-fns";

interface BasicDetailsTicket {
  ticketTitle: string;
  ticketNumber: string;
  slotNumber: string;
  image: string;
  ticketPrice: string;
  ticketDescription: string;
  startDate: any;
  closeDate: any;
  numberOfPack: string;
}


interface TicketFormProps {
  data: BasicDetailsTicket;
  onFormSubmit: (values: any) => void;
  loading: boolean;
  image?: any;
  setImage?: any;
  edit?: boolean;
}

const TicketForm: React.FC<TicketFormProps> = ({ loading, data, onFormSubmit, image, setImage, edit = false }) => {
  const { t } = useTranslation("translation");
  const [imageLoading, setImageLoading] = useState(false);

  //@ts-ignore
  const config = window.config;
  const numberOfPackMapping = config.numberOfPackMapping ?? {};

  const validationSchema = yup.object().shape({
    ticketTitle: yup.string().required(t("titleRequired")),
    ticketNumber: yup.string().required(t("ticketNumberRequired")),
    image: yup
      .mixed()
      .required(t("imageRequired")),
    // .test("fileType", t("fileUploadError"), (file: any) =>
    //   file ? ["image/png", "image/jpg", "image/jpeg"].includes(file.type) : true,
    // ),
    startDate: yup.string().required(t("startDateIsRequired")),
    closeDate: yup.string().required(t("closeDateIsRequired")),
    ticketPrice: yup.string().required(t("priceRequired")),
    ticketDescription: yup.string().required(t("descriptionRequired")),
  });


  const handleImageUpload = (event: any) => {
    const file = URL.createObjectURL(event?.target?.files[0]);
    setImage(file);
    const fileFound = event.target.type === "file" && event.target.files[0];
    setFieldValue("image", fileFound);
  };



  const formik = useFormik({
    initialValues: data,
    onSubmit: onFormSubmit,
    validationSchema: validationSchema,
    enableReinitialize: true,
  });

  const { values, errors, handleSubmit, touched, handleChange, setFieldValue, resetForm, handleReset } = formik;

  const onLoad = () => {
    setImageLoading(false);
  };

  const [startDate, setStartDate] = useState<any>(dayjs(data.startDate));
  const [closeDate, setCloseDate] = useState<any>(dayjs(data.closeDate));

  React.useEffect(() => {
    const keys = Object.keys(numberOfPackMapping);
    const value = values?.ticketPrice.toString();
      if (keys.includes(value))
        setFieldValue("numberOfPack", numberOfPackMapping[value])
      else
        setFieldValue("numberOfPack", "")
  }, [values.ticketPrice]);

  return (
    <Grid className='h-screen bg-white p-[2%]'>
      <Grid className='text-[1.25rem] font-[500]'>{t("ticketDetails")}</Grid>
      <Grid className='grid grid-cols-2 mt-1 gap-12'>
        <Grid className='col-span-1'>
          <Grid>
            <CommonInput
              id='title-id-input-box'
              fullWidth
              name='ticketTitle'
              label={t("ticketTitle")}
              placeholder={t("enterTicketTitle")}
              onChange={handleChange}
              value={values?.ticketTitle}
              sx={true}
              error={touched?.ticketTitle && errors?.ticketTitle ? true : false}
              helperText={errors?.ticketTitle && touched?.ticketTitle ? errors?.ticketTitle : ""}
              className="input-box"
            />
          </Grid>
          <Grid>
            <CommonInput
              id='title-id-input-box'
              fullWidth
              name='ticketNumber'
              label={t("ticketNumber")}
              placeholder={t("enterTicketNumber")}
              onChange={handleChange}
              value={values?.ticketNumber}
              sx={true}
              error={touched?.ticketNumber && errors?.ticketNumber ? true : false}
              helperText={errors?.ticketNumber && touched?.ticketNumber ? errors?.ticketNumber : ""}
              className="input-box"
            />
          </Grid>
          <Grid>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={['DatePicker']}>
                <DatePicker
                  className="date-field"
                  name='startDate'
                  label={t("startDate")}
                  onChange={(newValue) => {
                    setStartDate(newValue);
                    setFieldValue("startDate", newValue.format("MM-DD-YYYY"));
                  }}
                  value={startDate}
                  disablePast
                  slotProps={{
                    textField: {
                      error: touched?.startDate && errors?.startDate ? true : false,
                      helperText: errors?.startDate && touched?.startDate ?  errors?.startDate as string : "",
                      variant: 'standard',
                    },
                  }}
                  sx={{ width: "50%" }}
                />
              </DemoContainer>
            </LocalizationProvider>
          </Grid>
          <Grid className='relative'>
            <Grid className='text-[0.75rem]'>
              <Grid
                className={`${touched?.image && errors?.image ? "text-red-700" : "text-[#00000099]"
                  }`}
              >
                {t("ticketImage")}
              </Grid>
            </Grid>
            <UploadImage
              name='image'
              onChange={(event: any) => handleImageUpload(event)}
              accept='.png,.jpg,.jpeg,.gif'
            />
            {!image ? (
              <Grid>
                <Grid className='bg-[#f1f0ee] mt-1 w-[50%] text-[#00000099] text-[0.8rem] h-[80px] flex justify-center items-center rounded-[8px]'>
                  {t("uploadTicketImage")}
                </Grid>
                <FormHelperText
                  error={touched?.image && errors?.image ? true : false}
                  className='input-helper-text'
                >
                  {errors?.image && touched?.image ? errors?.image : ""}
                </FormHelperText>
              </Grid>
            ) : <Grid></Grid>}
            {!errors?.image ? (
              <Grid item xs={6} className='h-[110px]'>
                {image && (
                  <img
                    alt="lottery"
                    src={image}
                    onLoad={onLoad}
                    className='w-auto h-full aspect-square object-cover rounded-[20%] mt-2'
                  />
                )}
                {imageLoading && (
                  <CircularProgress
                    color='inherit'
                    size={14}
                    thickness={6}
                    style={{ marginLeft: "10%" }}
                  />
                )}
              </Grid>
            ) : <Grid item xs={6} className='h-[110px]'></Grid>}
          </Grid>
        </Grid>
        <Grid className='col-span-1'>
          <Grid>
            <CommonInput
              id='ticket-price-input-box'
              fullWidth
              name='ticketPrice'
              label={t("ticketPrice")}
              maxLength={10}
              sx={true}
              type={"number"}
              placeholder={t("specifyPriceOfTicket")}
              onChange={handleChange}
              value={values?.ticketPrice}
              error={touched?.ticketPrice && errors?.ticketPrice ? true : false}
              helperText={errors?.ticketPrice && touched?.ticketPrice ? errors?.ticketPrice : ""}
              className="input-box"
            />
          </Grid>
          <Grid>
            <CommonInput
              id='number-of-pack-input-box'
              fullWidth
              name='numberOfPack'
              label={t("numberOfPack")}
              placeholder={t("enterNumberOfPack")}
              onChange={handleChange}
              value={values?.numberOfPack}
              sx={true}
              disabled={edit || Object.keys(numberOfPackMapping).includes(values?.ticketPrice?.toString()) ? true : false}
              error={touched?.numberOfPack && errors?.numberOfPack ? true : false}
              helperText={errors?.numberOfPack && touched?.numberOfPack ? errors?.numberOfPack : ""}
              className="input-box"
            />
          </Grid>
          <Grid>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={['DatePicker']}>
                <DatePicker
                  className="date-field"
                  name='closeDate'
                  label={t("closeDate")}
                  onChange={(newValue) => {
                    setCloseDate(newValue);
                    setFieldValue("closeDate", newValue.format("MM-DD-YYYY"));
                  }}
                  value={closeDate}
                  minDate={startDate ? startDate : dayjs(new Date())}
                  slotProps={{
                    textField: {
                      error: touched?.closeDate && errors?.closeDate ? true : false,
                      helperText: errors?.closeDate && touched?.closeDate ? errors?.closeDate as string : "",
                      variant: 'standard',
                    },
                  }}
                  sx={{ width: "50%" }}
                />
              </DemoContainer>
            </LocalizationProvider>
          </Grid>
          <Grid>
            <CommonInput
              label={t("ticketDescription")}
              placeholder={t("enterTicketDescriptionHere")}
              minRows={8}
              maxRows={8}
              fullWidth
              multiline
              name='ticketDescription'
              onChange={handleChange}
              sx={true}
              value={values?.ticketDescription}
              error={touched?.ticketDescription && errors?.ticketDescription ? true : false}
              helperText={errors?.ticketDescription && touched?.ticketDescription ? errors?.ticketDescription : ""}
              className="input-box"
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid className='mt-2 h-[10%] flex justify-start gap-4'>
        <Grid className='w-[15%]'>
          <CommonButton
            id='submit-button'
            className='login-button'
            variant='contained'
            size='small'
            disabled={loading || (edit && JSON.stringify(values) === JSON.stringify(data))}
            onClick={() => {
              handleSubmit();
            }}
          >
            {edit ? t("updateButton", "Update") : t("save")}
            {loading && (
              <CircularProgress
                color='inherit'
                size={14}
                thickness={6}
                style={{ marginLeft: "8px" }}
              />
            )}
          </CommonButton>
        </Grid>
        <Grid className='w-[15%]'>
          <CommonButton
            id='reset-button'
            className='sign-up-button'
            variant='outlined'
            size='small'
            onClick={(e) => {
              setImage(data.image);
              handleReset(e);
            }}
          >
            {t("reset")}
          </CommonButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default TicketForm;
